import { useState } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { AlertTriangle, Shield } from 'lucide-react';
interface TCPAComplianceDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onConfirm: () => void;
  listName: string;
  isLoading?: boolean;
}
export function TCPAComplianceDialog({
  open,
  onOpenChange,
  onConfirm,
  listName,
  isLoading = false
}: TCPAComplianceDialogProps) {
  const [acknowledged, setAcknowledged] = useState(false);
  const handleConfirm = () => {
    if (acknowledged) {
      onConfirm();
      setAcknowledged(false);
      onOpenChange(false);
    }
  };
  const handleOpenChange = (newOpen: boolean) => {
    if (!newOpen) {
      setAcknowledged(false);
    }
    onOpenChange(newOpen);
  };
  return <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Shield className="h-5 w-5 text-orange-500" />
            Compliance acknowledgement
          </DialogTitle>
          <DialogDescription>
            Before you can use the prospect list "{listName}" for voice prospecting, 
            please acknowledge your responsibility for list compliance.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <Alert>
            <AlertTriangle className="h-4 w-4" />
            <AlertDescription>
              <strong>Important Notice:</strong> User confirms list compliance responsibility. 
              Please ensure your list complies with applicable outreach laws before proceeding.
            </AlertDescription>
          </Alert>

          <div className="bg-muted p-4 rounded-lg">
            <h4 className="font-semibold mb-2">Compliance Requirements:</h4>
            <p className="text-sm text-muted-foreground mb-3">
              Before proceeding, please review the current regulations and requirements to ensure full compliance.
            </p>
            <a href="https://www.fcc.gov/consumers/guides/stop-unwanted-robocalls-and-texts" target="_blank" rel="noopener noreferrer" className="inline-flex items-center text-sm text-primary hover:text-primary/80 underline">
              View FCC Guidelines & Requirements →
            </a>
          </div>

          <div className="border-2 border-orange-200 p-4 rounded-lg bg-slate-50">
            <div className="flex items-start space-x-3">
              <Checkbox id="tcpa-acknowledgment" checked={acknowledged} onCheckedChange={checked => setAcknowledged(checked === true)} className="mt-1" />
              <label htmlFor="tcpa-acknowledgment" className="text-sm font-medium leading-relaxed cursor-pointer">
                I acknowledge responsibility for ensuring this list complies with applicable outreach laws (including TCPA).
              </label>
            </div>
          </div>

          <div className="text-xs text-muted-foreground">
            By checking this box, you confirm that you understand and will comply with all 
            applicable outreach laws and regulations.
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => handleOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={handleConfirm} disabled={!acknowledged || isLoading} className="bg-green-600 hover:bg-green-700">
            {isLoading ? 'Creating List...' : 'Acknowledge & Continue'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>;
}